import { useCallback, useEffect, useMemo, useState } from "react";
import SearchVinForm from "./SearchVinForm.jsx";
import VehicleCard from "./VehicleCard.jsx";
import CategoryGrid, { CATEGORIES } from "./CategoryGrid.jsx";
import PartsTable from "./PartsTable.jsx";
import { getParts, getSearchProducts, postDecodeVin } from "@/utils/vinPartsApi";
import { prefetchRecaptchaPublicConfig, shouldShowRecaptchaPublicNotice } from "@/utils/recaptcha";

function normalizePathVin(pathname) {
  const norm = (pathname || "/").replace(/\/+$/, "") || "/";
  if (norm === "/piese-dupa-vin") return { mode: "home", vin: "" };
  const prefix = "/piese-dupa-vin/";
  if (!norm.startsWith(prefix)) return { mode: "unknown", vin: "" };
  const raw = norm.slice(prefix.length).split("/")[0] || "";
  const vin = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (vin.length < 7) return { mode: "invalid", vin };
  return { mode: "vehicle", vin };
}

export default function VinPartsPage({ t, pathname }) {
  const tp = t.vinPartsPage || {};
  const route = useMemo(() => normalizePathVin(pathname), [pathname]);

  const [vinInput, setVinInput] = useState("");
  const [vehicle, setVehicle] = useState(null);
  const [decodeLoading, setDecodeLoading] = useState(false);
  const [decodeError, setDecodeError] = useState("");

  const [category, setCategory] = useState(() => CATEGORIES[0]?.key || "brakes");
  const [parts, setParts] = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  const [partsError, setPartsError] = useState("");
  const [partsCatalogNotice, setPartsCatalogNotice] = useState("");

  const [productsByOem, setProductsByOem] = useState({});
  const [loadingOem, setLoadingOem] = useState("");
  const [showRecaptchaNotice, setShowRecaptchaNotice] = useState(false);

  useEffect(() => {
    let cancelled = false;
    prefetchRecaptchaPublicConfig().finally(() => {
      if (cancelled) return;
      setShowRecaptchaNotice(shouldShowRecaptchaPublicNotice());
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const syncCategoryUrl = useCallback((key) => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("category", key);
    window.history.replaceState({}, "", url.toString());
  }, []);

  const loadVehicleByVin = useCallback(async (vin) => {
    setDecodeLoading(true);
    setDecodeError("");
    setVehicle(null);
    setParts([]);
    setProductsByOem({});
    setPartsCatalogNotice("");

    const { ok, data } = await postDecodeVin(vin);
    setDecodeLoading(false);

    if (!ok || !data?.success) {
      const msg = typeof data?.error === "string" ? data.error : "";
      setDecodeError(msg || tp.errorGeneric);
      return;
    }

    setVehicle(data.vehicle);
  }, [tp.errorGeneric]);

  useEffect(() => {
    if (route.mode === "vehicle" && route.vin) {
      setVinInput(route.vin);
      loadVehicleByVin(route.vin);
    }
    if (route.mode === "home") {
      setVehicle(null);
      setDecodeError("");
      setParts([]);
      setProductsByOem({});
      setPartsCatalogNotice("");
    }
  }, [route.mode, route.vin, loadVehicleByVin]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const c = new URLSearchParams(window.location.search).get("category");
    if (c && CATEGORIES.some((x) => x.key === c)) {
      setCategory(c);
    }
  }, [pathname]);

  useEffect(() => {
    if (!vehicle?.id || !category) return;

    let cancelled = false;
    (async () => {
      setPartsLoading(true);
      setPartsError("");
      setPartsCatalogNotice("");
      const { ok, data } = await getParts(vehicle.id, category);
      if (cancelled) return;
      setPartsLoading(false);
      if (!ok || !data?.success) {
        setParts([]);
        setPartsCatalogNotice("");
        setPartsError(data?.error || tp.errorGeneric);
        return;
      }
      setParts(data.parts || []);
      setPartsCatalogNotice(
        typeof data.parts_catalog_notice === "string" ? data.parts_catalog_notice : "",
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [vehicle?.id, category, tp.errorGeneric]);

  const onSubmitSearch = async () => {
    const v = vinInput.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (v.length < 7) {
      setDecodeError(tp.errorGeneric);
      return;
    }
    const target = `/piese-dupa-vin/${v}`;
    window.location.href = target;
  };

  const onSelectCategory = (key) => {
    setCategory(key);
    syncCategoryUrl(key);
    setProductsByOem({});
  };

  const onSearchProducts = async (oem) => {
    setLoadingOem(oem);
    const { ok, data } = await getSearchProducts(oem);
    setLoadingOem("");
    if (!ok || !data?.success) {
      setProductsByOem((prev) => ({ ...prev, [oem]: [] }));
      return;
    }
    setProductsByOem((prev) => ({ ...prev, [oem]: data.products || [] }));
  };

  if (route.mode === "invalid") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <p className="text-red-700">{tp.errorGeneric}</p>
        <button
          type="button"
          className="mt-4 text-sm text-[#0b378c] underline"
          onClick={() => {
            window.location.href = "/piese-dupa-vin";
          }}
        >
          {tp.back}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-[#f7f8fa] min-h-[60vh]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <header className="text-center mb-10">
          <p className="text-[11px] font-mono uppercase tracking-[0.28em] text-slate-500 mb-3">
            Selena · tool
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0b1220] tracking-tight">{tp.title}</h1>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto leading-relaxed">{tp.subtitle}</p>
        </header>

        {showRecaptchaNotice ? (
          <div
            data-testid="vin-parts-recaptcha-info"
            className="max-w-3xl mx-auto mb-10 rounded border border-[#c7d2eb] bg-[#f0f4fb] px-4 py-4 sm:px-6"
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#0b378c] mb-2">
              {tp.recaptchaInfoTitle}
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">{tp.recaptchaInfoBody}</p>
            <p className="mt-3 text-[10px] text-slate-600 leading-relaxed">
              <span>{tp.recaptchaLegalLead}</span>{" "}
              <a
                href="https://policies.google.com/privacy"
                className="text-[#0b378c] underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {tp.recaptchaPrivacy}
              </a>
              {" · "}
              <a
                href="https://policies.google.com/terms"
                className="text-[#0b378c] underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {tp.recaptchaTerms}
              </a>
            </p>
          </div>
        ) : null}

        {route.mode === "home" ? (
          <SearchVinForm
            t={tp}
            value={vinInput}
            onChange={setVinInput}
            onSubmit={onSubmitSearch}
            disabled={decodeLoading}
            error={decodeError}
            recaptchaSetupHint={tp.recaptchaLocalhostHint}
          />
        ) : null}

        {route.mode === "vehicle" ? (
          <div className="space-y-8">
            <button
              type="button"
              className="text-xs font-mono uppercase tracking-[0.18em] text-[#0b378c] hover:underline"
              onClick={() => {
                window.location.href = "/piese-dupa-vin";
              }}
            >
              ← {tp.back}
            </button>

            <SearchVinForm
              t={tp}
              value={vinInput}
              onChange={setVinInput}
              onSubmit={onSubmitSearch}
              disabled={decodeLoading}
              error={decodeError}
              recaptchaSetupHint={tp.recaptchaLocalhostHint}
            />

            {decodeLoading ? (
              <p className="text-sm text-slate-600" role="status">
                {tp.loading}
              </p>
            ) : null}

            {!decodeLoading && vehicle ? (
              <>
                <VehicleCard t={tp} vehicle={vehicle} />
                <CategoryGrid
                  t={tp}
                  selectedKey={category}
                  disabled={!vehicle?.id}
                  onSelect={onSelectCategory}
                />
                {partsError ? (
                  <p className="text-sm text-red-700" role="alert">
                    {partsError}
                  </p>
                ) : null}
                <PartsTable
                  t={tp}
                  parts={parts}
                  loading={partsLoading}
                  catalogNotice={partsCatalogNotice}
                  realoemUrl={vehicle?.source_url || ""}
                  vehicleVinFull={vehicle?.vin_full || ""}
                  vehicleVinLast7={vehicle?.vin_last7 || ""}
                  vehicleBrand={vehicle?.brand || ""}
                  productsByOem={productsByOem}
                  loadingOem={loadingOem}
                  onSearchProducts={onSearchProducts}
                />
              </>
            ) : null}
          </div>
        ) : null}

        <p className="mt-12 text-center text-xs text-slate-500 max-w-2xl mx-auto leading-relaxed border border-dashed border-[#d7deea] rounded-lg p-4 bg-white/60">
          {tp.disclaimer}
        </p>
      </div>
    </div>
  );
}
